import { Observation } from "@medplum/fhirtypes";
import { FhirBundleSdk, Smart } from "@metriport/fhir-sdk";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
import {
  getObservationCode as getObservationCodeForGrouping,
  getObservationDate,
  groupResourcesByCode,
  ObservationSeries,
} from "../../table-transforms/series-utils";
import { getObservationValue } from "../../utils/observation";

export type SocialHistoryRowData = {
  id: string;
  observation: string;
  value: string;
  date: string;
  originalData: ObservationSeries;
};

export function getSocialHistoryRows({
  bundle,
}: {
  bundle: FhirBundleSdk;
}): SocialHistoryRowData[] {
  // Get only social history observations (category = "social-history")
  const observations = bundle.getObservations().filter(observation => {
    return (
      observation.category?.some(cat =>
        cat.coding?.some(coding => coding.code?.toLowerCase() === "social-history")
      ) ?? false
    );
  });

  // Group observations by code into series
  const observationSeries = groupResourcesByCode<Smart<Observation>>(
    observations,
    getObservationCodeForGrouping,
    getObservationDate,
    "Observation"
  );

  // Convert series to row data
  return observationSeries.map(series => {
    const latestObservation = series.resources[series.resources.length - 1]; // Last in array is newest

    if (!latestObservation) {
      throw new Error("Series should always have at least one resource");
    }

    return {
      id: `series-${series.code}`,
      observation:
        latestObservation.code?.coding?.[0]?.display || latestObservation.code?.text || "-",
      value: getObservationValue(latestObservation),
      date: formatDate(series.latestDate),
      originalData: series,
    };
  });
}

function formatDate(date: string | undefined): string {
  if (!date || date === "-") return "-";
  try {
    const parsed = dayjs(date);
    if (!parsed.isValid()) return "-";
    return parsed.format(ISO_DATE);
  } catch {
    return "-";
  }
}
